'use client'

import React from 'react'
import { usePathname, useSearchParams, useRouter } from 'next/navigation'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import { BackgroundTextGlass } from '@/components/ui/BackgroundTextGlass'

interface ShopPaginationProps {
  currentPage: number
  totalPages: number
}

export const ShopPagination: React.FC<ShopPaginationProps> = ({ currentPage, totalPages }) => {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const createPageURL = (pageNumber: number) => {
    const params = new URLSearchParams(searchParams.toString())
    if (pageNumber === 1) {
      params.delete('page')
    } else {
      params.set('page', pageNumber.toString())
    }
    const newParams = params.toString()
    return pathname + (newParams ? '?' + newParams : '')
  }

  const handlePageChange = (pageNumber: number) => {
    if (pageNumber < 1 || pageNumber > totalPages) return
    router.push(createPageURL(pageNumber), { scroll: true })
  }

  // Don't show pagination if there's only one page
  if (totalPages <= 1) return null

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | 'ellipsis')[] = []
    const maxPagesToShow = 5

    if (totalPages <= maxPagesToShow) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      // Always show first page
      pages.push(1)

      // Calculate range around current page
      const start = Math.max(2, currentPage - 1)
      const end = Math.min(totalPages - 1, currentPage + 1)

      // Add ellipsis after first page if needed
      if (start > 2) {
        pages.push('ellipsis')
      }

      // Add pages around current page
      for (let i = start; i <= end; i++) {
        pages.push(i)
      }

      // Add ellipsis before last page if needed
      if (end < totalPages - 1) {
        pages.push('ellipsis')
      }

      // Always show last page
      pages.push(totalPages)
    }

    return pages
  }

  const pageNumbers = getPageNumbers()

  return (
    <div className="flex justify-center py-1">
      <BackgroundTextGlass className="py-1 px-2 md:py-1.5 md:px-3 w-fit scale-90 md:scale-[0.85] origin-bottom shadow-md">
        <Pagination className="mx-0">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href={createPageURL(currentPage - 1)}
                onClick={(e) => {
                  e.preventDefault()
                  handlePageChange(currentPage - 1)
                }}
                className={
                  (currentPage === 1 ? 'pointer-events-none opacity-50 ' : 'cursor-pointer ') +
                  'text-white hover:text-white hover:bg-white/10'
                }
              />
            </PaginationItem>

            {pageNumbers.map((pageNum, index) => (
              <PaginationItem key={index}>
                {pageNum === 'ellipsis' ? (
                  <PaginationEllipsis className="text-white" />
                ) : (
                  <PaginationLink
                    href={createPageURL(pageNum)}
                    onClick={(e) => {
                      e.preventDefault()
                      handlePageChange(pageNum)
                    }}
                    isActive={currentPage === pageNum}
                    className={
                      currentPage === pageNum
                        ? 'bg-white text-emerald-600 hover:bg-white/90 hover:text-emerald-700 border-none'
                        : 'text-white hover:text-white hover:bg-white/10 cursor-pointer'
                    }
                  >
                    {pageNum}
                  </PaginationLink>
                )}
              </PaginationItem>
            ))}

            <PaginationItem>
              <PaginationNext
                href={createPageURL(currentPage + 1)}
                onClick={(e) => {
                  e.preventDefault()
                  handlePageChange(currentPage + 1)
                }}
                className={
                  (currentPage === totalPages
                    ? 'pointer-events-none opacity-50 '
                    : 'cursor-pointer ') + 'text-white hover:text-white hover:bg-white/10'
                }
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </BackgroundTextGlass>
    </div>
  )
}
